import imageCompression from 'browser-image-compression';
import { getUploadConfig } from './config';
import { getFopsRes } from './fops';
import { fetchObsURLWH, uploadFileToObs } from './obs';
import { UploadStatusCode, type FileType, type IImageUploadOptions, type IImageUploadResult } from './types';
import { blobToFile, file2Image, fileToBase64, getFileType, normalizeImageFile } from './utils/file';
import { urlToImage } from './utils/image';

export const defaultFileType: FileType[] = ['image/png', 'image/jpg', 'image/jpeg', 'image/heic', 'image/webp'];

interface UploadFlowResult {
    code: UploadStatusCode;
    newW?: number;
    newH?: number;
    file?: File;
    obsURL?: string;
}

/**
 * 文件合规校验、上传
 * @param fileData 文件数据
 * @param options
 */
class ImageUploader {
    // 图片宽高
    private __getImageDimensions(file: File): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const objectURL = URL.createObjectURL(file);
            img.onload = () => {
                URL.revokeObjectURL(objectURL);
                resolve({
                    width: img.width,
                    height: img.height,
                });
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectURL);
                reject(new Error('load image error'));
            };
            img.src = objectURL;
        });
    }

    // 本地处理
    private async __processLocally(file: File, options?: IImageUploadOptions): Promise<IImageUploadResult> {
        const { debug, accessToken, signature } = options || {};
        const autoResizeWH = options?.limitConfig?.autoResizeWH;
        const maxWH = options?.limitConfig?.maxWH;
        const minWH = options?.limitConfig?.minWH;
        const extraOutput = options?.extraOutput;

        // 上传桶
        const maatType = options?.customMaatType || getUploadConfig().type;
        const fileType = await getFileType(file);

        // 格式转换后的图片
        let convertFile = file;
        let processedFile = file;
        let error: any = new Error();
        let newW = 0;
        let newH = 0;
        let extraOutputImg: HTMLImageElement | string | undefined;

        // 1. 格式转换
        if (
            fileType === 'image/heic' ||
            (options?.autoConvertWebp && fileType === 'image/webp') ||
            (options?.autoConvertGif && fileType === 'image/gif')
        ) {
            const { code, file: resFile } = await this.__convertToJpegLocally(processedFile, options?.convertType);
            if (code === 0 && resFile) {
                processedFile = resFile;
                convertFile = resFile;
            } else {
                error.code = code;
                throw error;
            }
        }

        // 2. 最小尺寸校验
        const whInfo = await this.__getImageDimensions(processedFile);
        newW = whInfo.width;
        newH = whInfo.height;

        if (minWH) {
            if (minWH && (whInfo.width < minWH || whInfo.height < minWH)) {
                error.code = UploadStatusCode.ImageTooSmall;
                throw error;
            }
        }

        // 3. 优先自动尺寸调整
        if (autoResizeWH) {
            if (whInfo.width > autoResizeWH || whInfo.height > autoResizeWH) {
                const {
                    code,
                    file: resFile,
                    newW: resizeW,
                    newH: resizeH,
                } = await this.__resizeLocally(processedFile, autoResizeWH);
                if (code === 0 && resFile) {
                    processedFile = resFile;
                    newW = resizeW!;
                    newH = resizeH!;
                } else {
                    error.code = code;
                    throw error;
                }
            }
        }

        // 4. 最大尺寸超限制校验
        if (!autoResizeWH && maxWH) {
            if (maxWH && (whInfo.width > maxWH || whInfo.height > maxWH)) {
                error.code = UploadStatusCode.ImageTooLarge;
                throw error;
            }
        }

        // 5. 额外本地图片获取。支持指的尺寸或者直接原尺寸返回
        if (extraOutput) {
            let extraFile = convertFile;
            if (extraOutput.maxWH && (whInfo.width > extraOutput.maxWH || whInfo.height > extraOutput.maxWH)) {
                const { code, file: resFile } = await this.__resizeLocally(convertFile, extraOutput.maxWH);
                if (code === 0 && resFile) {
                    extraFile = resFile;
                } else {
                    error.code = code;
                    throw error;
                }
            }
            extraOutputImg =
                extraOutput.type === 'ImageURL' ? URL.createObjectURL(extraFile) : await file2Image(extraFile);
        }

        if (options?.noUploadObs) {
            // 不上传到obs
            const url = await fileToBase64(processedFile);
            return {
                url,
                width: newW,
                height: newH,
                processedFile,
                extraOutputImg: extraOutputImg,
            };
        }

        const { url } = await uploadFileToObs(processedFile, {
            debug,
            accessToken,
            signature,
            customMaatType: maatType,
            customSuffix: options?.customSuffix,
            partSize: options?.partSize,
            maxConcurrent: options?.maxConcurrent,
            onUploadProgress: options?.onUploadProgress,
        });

        return {
            url,
            width: newW,
            height: newH,
            processedFile,
            extraOutputImg: extraOutputImg,
        };
    }

    // 本地格式转换
    private async __convertToJpegLocally(
        file: File,
        targetFileType?: 'image/png' | 'image/jpeg',
    ): Promise<UploadFlowResult> {
        const targetType = targetFileType || 'image/png';
        const targetExtension = targetType === 'image/png' ? '.png' : '.jpeg';
        const fileType = await getFileType(file);
        try {
            // 使用 heic2any 转换
            if (fileType === 'image/heic') {
                const heic2any = await import('heic2any');
                const jpegBlob = (await heic2any.default({
                    blob: file,
                    toType: targetType,
                })) as Blob;
                const resFile = new File([jpegBlob], file.name.replace(/\.[^/.]+$/, targetExtension), {
                    type: targetType,
                });
                return { code: 0, file: resFile };
            }
            // 使用 Canvas 转换 webp、gif
            if (fileType === 'image/webp' || fileType === 'image/gif') {
                const bitmap = await createImageBitmap(file);
                const canvas = document.createElement('canvas');
                canvas.width = bitmap.width;
                canvas.height = bitmap.height;

                const ctx = canvas.getContext('2d')!;
                ctx.drawImage(bitmap, 0, 0);

                const blob = await new Promise<Blob>((resolve, reject) => {
                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                resolve(blob);
                            } else {
                                reject(new Error('toBlob is fail'));
                            }
                        },
                        targetType,
                        0.92,
                    );
                });

                const resFile = new File([blob], file.name.replace(/\.[^/.]+$/, targetExtension), {
                    type: targetType,
                });
                return { code: 0, file: resFile };
            }
            return { code: UploadStatusCode.ConvertError };
        } catch (error) {
            return { code: UploadStatusCode.ConvertError };
        }
    }

    // 本地尺寸裁剪
    private async __resizeLocally(file: File, resizeWH: number): Promise<UploadFlowResult> {
        try {
            const fileOrBlob = await imageCompression(file, {
                maxWidthOrHeight: resizeWH,
                maxIteration: 4,
                useWebWorker: true,
            });
            const newFile = fileOrBlob instanceof File ? fileOrBlob : blobToFile(fileOrBlob, file.name);

            const bitmap = await createImageBitmap(newFile);
            // 计算新尺寸
            const { width, height } = bitmap;

            return { code: 0, file: newFile, newW: width, newH: height };
        } catch (error) {
            return { code: UploadStatusCode.ResizeError };
        }
    }

    // 云端处理
    private async __processOnServer(file: File, options?: IImageUploadOptions): Promise<IImageUploadResult> {
        const { debug, accessToken, signature } = options || {};
        const autoResizeWH = options?.limitConfig?.autoResizeWH;
        const maxWH = options?.limitConfig?.maxWH;
        const minWH = options?.limitConfig?.minWH;
        const extraOutput = options?.extraOutput;
        const fileType = await getFileType(file);

        // 上传桶
        const maatType = options?.customMaatType || getUploadConfig().type;

        const { url } = await uploadFileToObs(file, {
            debug,
            accessToken,
            signature,
            customMaatType: maatType,
            customSuffix: options?.customSuffix,
            partSize: options?.partSize,
            maxConcurrent: options?.maxConcurrent,
            onUploadProgress: options?.onUploadProgress,
        });

        let resURL = url;
        let error: any = new Error();
        let convertURL = url;
        let extraOutputImg: HTMLImageElement | string | undefined;

        if (
            fileType === 'image/heic' ||
            (options?.autoConvertWebp && fileType === 'image/webp') ||
            (options?.autoConvertGif && fileType === 'image/gif')
        ) {
            const { code, obsURL } = await this.__convertToJpegOnServer(url, {
                maatType,
                fileType: options?.convertType,
            });
            if (code === 0 && obsURL) {
                resURL = obsURL;
                convertURL = obsURL;
            } else {
                error.code = code;
                throw error;
            }
        }

        // 2. 最小尺寸校验
        const whInfo = await fetchObsURLWH(resURL);
        let newW = whInfo.width;
        let newH = whInfo.height;

        if (minWH) {
            if (minWH && (whInfo.width < minWH || whInfo.height < minWH)) {
                error.code = UploadStatusCode.ImageTooSmall;
                throw error;
            }
        }

        // 3. 优先自动尺寸调整
        if (autoResizeWH) {
            if (whInfo.width > autoResizeWH || whInfo.height > autoResizeWH) {
                const { code, obsURL } = await this.__resizeOnServer(resURL, {
                    maatType,
                    newWH: autoResizeWH,
                });

                if (code === 0 && obsURL) {
                    // 重新获取一下新图片宽高
                    const newWhInfo = await fetchObsURLWH(obsURL);
                    newW = newWhInfo.width;
                    newH = newWhInfo.height;
                    resURL = obsURL;
                } else {
                    error.code = code;
                    throw error;
                }
            }
        }

        // 4. 尺寸超限制校验
        if (!autoResizeWH && maxWH) {
            if (maxWH && (whInfo.width > maxWH || whInfo.height > maxWH)) {
                error.code = UploadStatusCode.ImageTooLarge;
                throw error;
            }
        }

        // 5. 额外本地图片获取。支持指的尺寸或者直接原尺寸返回
        if (extraOutput) {
            let extraURL = convertURL;
            if (extraOutput.maxWH && (whInfo.width > extraOutput.maxWH || whInfo.height > extraOutput.maxWH)) {
                const { code, obsURL } = await this.__resizeOnServer(convertURL, {
                    maatType,
                    newWH: extraOutput.maxWH,
                });
                if (code === 0 && obsURL) {
                    extraURL = obsURL;
                } else {
                    error.code = code;
                    throw error;
                }
            }
            extraOutputImg = extraOutput.type === 'ImageURL' ? extraURL : await urlToImage(extraURL);
        }

        return { url: resURL, width: newW, height: newH, extraOutputImg };
    }

    /**
     * 云端格式转换
     * @doc: https://cf.meitu.com/confluence/pages/viewpage.action?pageId=70034759
     */
    private async __convertToJpegOnServer(
        obsURL: string,
        options: { maatType: string; fileType?: 'image/png' | 'image/jpeg' },
    ): Promise<UploadFlowResult> {
        try {
            const { maatType, fileType } = options;
            const targetType = fileType || 'image/png';
            const format = targetType === 'image/png' ? 'png' : 'jpeg';
            const fops = `imageView2/2/format/${format}`;

            const { status, url } = await getFopsRes(obsURL, maatType, fops);
            if (status === 2 && url) {
                return { code: 0, obsURL: url };
            }
            throw new Error('fops error');
        } catch (error) {
            return { code: UploadStatusCode.ConvertError };
        }
    }

    /**
     * 云端尺寸裁剪, 长边对齐，短边自适应
     * @doc: https://cf.meitu.com/confluence/pages/viewpage.action?pageId=70034759
     */
    private async __resizeOnServer(
        obsURL: string,
        options: { maatType: string; newWH: number },
    ): Promise<UploadFlowResult> {
        try {
            const { maatType, newWH } = options;
            const fops = `imageView2/0/w/${newWH}`;
            const { status, url } = await getFopsRes(obsURL, maatType, fops);
            if (status === 2 && url) {
                return { code: 0, obsURL: url };
            }
            throw new Error('fops error');
        } catch (error) {
            return { code: UploadStatusCode.ResizeError };
        }
    }

    // 文件校验并上传
    async checkAndUploadFileToObs(file: File, options?: IImageUploadOptions): Promise<IImageUploadResult> {
        const { debug } = options || {};
        const defaultOptions: IImageUploadOptions = {
            autoConvertWebp: true,
            autoConvertGif: true,
        };
        const finalOptions = { ...defaultOptions, ...options };
        const accepts = finalOptions?.limitConfig?.accepts || defaultFileType;
        const fileType = await getFileType(file);

        try {
            let err: any = new Error();
            let newFile = file;

            // 添加调试fop后门
            if (location.search.match('debugfop')) {
                err.code = UploadStatusCode.ConvertError;
                throw err;
            }

            if (!accepts.includes(fileType as FileType)) {
                // 格式不支持
                err.code = UploadStatusCode.TypeError;
                throw err;
            }
            if (finalOptions?.limitConfig?.maxSize && file.size > finalOptions?.limitConfig?.maxSize) {
                // 体积超限
                err.code = UploadStatusCode.FileTooLarge;
                throw err;
            }

            // 移动端拍照旋转问题，jpg
            newFile = await normalizeImageFile(newFile);

            // 优先本地处理
            return await this.__processLocally(newFile, finalOptions);
        } catch (error: any) {
            if (debug) {
                console.error('checkAndUploadFileToObs: error =>', error);
            }

            // 格式转换或者裁剪失败，降级到云端处理
            if ([UploadStatusCode.ConvertError, UploadStatusCode.ResizeError].includes(error.code)) {
                return await this.__processOnServer(file, finalOptions);
            }

            if (error instanceof Response) {
                const json = await error.json();
                throw new Error(`${json.code} ${json.message}`);
            }

            throw error;
        }
    }
}

export const imageUploader = new ImageUploader();
